import { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ArrowRight, Sparkle } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';

const messages = defineMessages({
  badge: {
    id: 'search.signInCta.badge',
    defaultMessage: 'Sync your search',
    description: 'Badge text for sign-in call-to-action banner encouraging users to sync their search',
  },
  title: {
    id: 'search.signInCta.title',
    defaultMessage: 'Save your job hunt everywhere',
    description: 'Title for sign-in call-to-action banner encouraging users to sign in to sync their job search',
  },
  description: {
    id: 'search.signInCta.description',
    defaultMessage: 'Sign in to keep saved jobs, searches, and progress synced across every device.',
    description: 'Description text for sign-in call-to-action explaining the benefits of signing in',
  },
  button: {
    id: 'search.signInCta.button',
    defaultMessage: 'Sign in to sync progress',
    description: 'Button label for sign-in call-to-action to sign in and sync job search progress',
  },
});

export type SignInProgressCtaProps = {
  className?: string;
};

export function SignInProgressCta({ className }: SignInProgressCtaProps) {
  const { isAuthenticated, signIn } = useAuth();
  const loginHref = '/auth/login';


  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      signIn();
    },
    [signIn]
  );

  const callToActionLabel = useMemo(
    () => (
      <span className="flex items-center gap-2">
        <FormattedMessage {...messages.button} />
        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    ),
    []
  );

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className={className}>
      <div className="relative overflow-hidden rounded-[28px] border border-white/40 bg-gradient-to-br from-[#3B5E45] via-[#5A8A68] to-[#3B5E45] text-[#F2EADF] shadow-[0_24px_48px_-20px_rgba(59,94,69,0.45)] dark:border-[#98CFA8]/25 dark:shadow-none dark:from-[#1A2E22] dark:via-[#2A4533] dark:to-[#1A2E22]">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -left-10 top-6 h-40 w-40 rounded-full bg-white/20 blur-3xl dark:bg-[#98CFA8]/15" />
          <div className="absolute bottom-[-60px] right-[-20px] h-48 w-48 rounded-full bg-[#C8DDCE]/30 blur-3xl dark:bg-[#5A8A68]/15" />
        </div>

        <div className="relative flex flex-col gap-4 px-4 py-5 sm:gap-6 sm:px-6 sm:py-8 lg:px-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center justify-center lg:justify-start">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-xl shadow-inner shadow-white/10 sm:h-16 sm:w-16">
              <Sparkle className="h-6 w-6 text-white sm:h-8 sm:w-8" />
            </div>
          </div>

          <div className="flex-1 text-center lg:text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/80 sm:text-xs md:text-sm">
              <FormattedMessage {...messages.badge} />
            </p>
            <h2 className="mt-2 text-xl font-semibold sm:mt-3 sm:text-2xl md:text-3xl lg:text-[32px]">
              <FormattedMessage {...messages.title} />
            </h2>
            <p className="mt-2 text-sm text-white/90 sm:mt-3 sm:text-base md:text-lg">
              <FormattedMessage {...messages.description} />
            </p>
          </div>

          <div className="flex w-full justify-center lg:w-auto lg:justify-end">
            <a
              href={loginHref}
              onClick={handleClick}
              className="group relative inline-flex w-full items-center justify-center rounded-2xl bg-[#F8F5F0] px-4 py-2.5 text-sm font-semibold text-[#3B5E45] shadow-lg shadow-black/10 dark:shadow-none transition-all duration-200 hover:shadow-xl dark:bg-[#F2EADF] dark:text-[#2A4533] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3B5E45] sm:px-5 sm:py-3 sm:text-base md:w-auto"
            >
              {callToActionLabel}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignInProgressCta;


